import { useState, useEffect, useMemo } from "react";
import { Select } from "antd";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getCities } from "services/SauceService";
import { countriesUK, provincesCanada, statesUS } from "./data";
import { BaseFilterProps } from "interfaces/filter-component-base";
import { getCountryFlagComponent } from "./CountryFlags";
import "./LocationFilter.scss";

const getCountryOptions = (countryCode: string | null | undefined) => {
  if (!countryCode) {
    return [];
  }

  if (countryCode === "US") {
    return statesUS;
  } else if (countryCode === "CA") {
    return provincesCanada;
  } else if (countryCode === "UK") {
    return countriesUK;
  }
  return [];
};

const getStateLabel = (countryCode: string | null | undefined) => {
  if (countryCode === "US") {
    return "State";
  } else if (countryCode === "CA") {
    return "Province";
  } else if (countryCode === "UK") {
    return "County";
  }
  return "State";
};

const countryOptions = [
  {
    value: "US",
    label: (
      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {getCountryFlagComponent("US")}
        United States
      </span>
    ),
  },
  {
    value: "CA",
    label: (
      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {getCountryFlagComponent("CA")}
        Canada
      </span>
    ),
  },
  {
    value: "UK",
    label: (
      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {getCountryFlagComponent("UK")}
        United Kingdom
      </span>
    ),
  },
];

const LocationFilter = (props: BaseFilterProps) => {
  const { onChangeValues, values } = props;

  const [loading, setLoading] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [cityOptions, setCityOptions] = useState<any[]>([]);
  const [locationValues, setLocationValues] = useState<{
    country?: string | null;
    state?: string | null;
    city?: string | null;
  }>({
    country: "US",
  });

  useEffect(() => {
    const locations = values?.length > 0 ? values : [];
    setSelectedLocations(locations || []);
  }, [values]);

  const stateOptions = useMemo(() => {
    return getCountryOptions(locationValues?.country);
  }, [locationValues?.country]);

  const stateLabel = useMemo(() => {
    return getStateLabel(locationValues?.country);
  }, [locationValues?.country]);

  const handleChangeState = async (value: string | null) => {
    if (value === locationValues.state || !value) return;
    setLoading(true);

    let params: any = {
      countryCode: locationValues.country,
    };
    if (locationValues.country === "US") {
      params.stateCode = value;
    } else if (locationValues.country === "CA") {
      params.provinceCode = value;
    } else if (locationValues.country === "UK") {
      params.countryName = value;
    }
    const response: any = await getCities(params);
    const auxCityOptions = response?.map((city: any) => ({
      value: city.city,
      label: city.city,
    }));

    setCityOptions(auxCityOptions);
    setLoading(false);
  };

  const handleChange = async (
    field: "country" | "state" | "city",
    value: string | null
  ) => {
    let newValues: {
      country?: string | null;
      state?: string | null;
      city?: string | null;
    } = {
      ...locationValues,
      [field]: value,
    };

    if (field === "country") {
      newValues.state = null;
      newValues.city = null;
    } else if (field === "state") {
      newValues.city = null;
      handleChangeState(value);
    }

    setLocationValues(newValues);
  };

  const handleSelectCity = (value: string) => {
    setLocationValues((prev) => {
      return {
        country: prev.country,
        state: prev.state,
        city: null,
      };
    });

    if (!value) {
      return;
    }
    const newValue = `${locationValues.country}|${locationValues.state}|${value}`;

    if (selectedLocations.includes(newValue)) {
      return;
    }

    const newSelectedLocations = [...selectedLocations, newValue];
    setSelectedLocations([...newSelectedLocations]);

    if (onChangeValues) {
      onChangeValues(newSelectedLocations);
    }
  };

  const handleRemoveLocation = (removeLocation: string) => {
    const newSelectedLocations = selectedLocations.filter(
      (location) => location !== removeLocation
    );
    setSelectedLocations(newSelectedLocations);
    if (onChangeValues) {
      onChangeValues(newSelectedLocations);
    }
  };

  const renderSelectedCountryBadge = (location: string) => {
    const country = location.split("|")[0];
    const countryOption = getCountryOptions(country);
    const state = location.split("|")[1];
    const stateLabel =
      countryOption?.find((option) => option.value === state)?.label ?? state;
    const city = location.split("|")[2];
    const iconFlag = getCountryFlagComponent(country);

    const renderText = () => {
      if (city) {
        return `${city}, ${stateLabel}`;
      } else if (stateLabel) {
        return stateLabel;
      } else if (country) {
        return country;
      }
      return null;
    };

    return (
      <div className="selected-location-item" key={location}>
        {iconFlag && (
          <div className="selected-location-item-country">{iconFlag}</div>
        )}
        <div>{renderText()}</div>

        <FontAwesomeIcon
          icon={faXmark}
          className="remove-location-icon"
          onClick={() => handleRemoveLocation(location)}
        />
      </div>
    );
  };

  return (
    <div className="location-filter">
      <div className="location-filter-item">
        <label className="location-filter-label">Country</label>
        <Select
          placeholder="Select a country"
          value={locationValues.country}
          onChange={(value) => handleChange("country", value)}
          options={countryOptions}
          rootClassName="location-filter-select"
        />
      </div>
      <div
        className={`location-filter-item ${
          !locationValues.country ? "hidden" : ""
        }`}
      >
        <label className="location-filter-label">{stateLabel}</label>
        <Select
          options={stateOptions}
          placeholder={`Select a ${stateLabel.toLowerCase()}`}
          value={locationValues.state}
          onChange={(value) => handleChange("state", value)}
          rootClassName="location-filter-select"
          optionFilterProp="label"
          showSearch
        />
      </div>
      <div
        className={`location-filter-item ${
          !locationValues.state ? "hidden" : ""
        }`}
      >
        <label className="location-filter-label">City</label>
        <Select
          loading={loading}
          disabled={loading}
          placeholder="Select a City"
          value={locationValues.city}
          onChange={(value) => handleSelectCity(value)}
          options={cityOptions}
          rootClassName="location-filter-select"
          optionFilterProp="label"
          showSearch
        />
      </div>
      <div className="selected-locations-container">
        <label className="location-filter-label">Selected Locations</label>
        <div className="selected-location-items">
          {selectedLocations?.map((location) =>
            renderSelectedCountryBadge(location)
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationFilter;
